import { Component, Input, signal, inject, OnInit, output, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProjectService } from '../../../../core/services/project.service';
import { Project } from '../../../../core/models/models';
import { ComptageUtils } from '../../../../core/utils/comptage-utils';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { ComptageRuleService } from '../../../../core/services/comptage-rule.service';
import { ComptageRule } from '../../../../core/models/models';

@Component({
  selector: 'app-project-comptage',
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, MatSelectModule, MatSnackBarModule],
  templateUrl: './project-comptage.component.html',
  styleUrl: './project-comptage.component.scss'
})
export class ProjectComptageComponent implements OnInit {
  private projectService = inject(ProjectService);
  private ruleService = inject(ComptageRuleService);
  private snackBar = inject(MatSnackBar);
  private cdr = inject(ChangeDetectorRef);

  @Input({ required: true }) project!: Project;
  @Input() readonlyMode = false;
  saved = output<void>();
  error = output<string>();

  activeRule = signal<ComptageRule | undefined>(undefined);
  showRuleDialog = signal(false);
  localErrorMessage = signal('');

  voltageOptions: string[] = [];
  puissanceOptions: string[] = [];
  puissance2Options: string[] = [];
  typeOptions: string[] = ['Huile', 'Sec'];
  type2Options: string[] = ['N/A', 'Huile', 'Sec'];

  ngOnInit(): void {
    this.ruleService.getActiveRule().subscribe({
      next: (rule) => {
        this.activeRule.set(rule);
        this.parseOptions(rule);
        this.cdr.markForCheck();
      },
      error: () => {
        console.log('No active rule found, using fallback options');
        this.setDefaultOptions();
        this.cdr.markForCheck();
      }
    });
  }

  private parseOptions(rule: ComptageRule): void {
    if (rule.voltageOptions) {
      this.voltageOptions = rule.voltageOptions.split(',')
        .map(s => s.trim())
        .filter(s => s !== '')
        .map(s => s.toLowerCase().endsWith('kv') ? s : `${s} kV`);
    }

    if (rule.puissanceOptions) {
      const pOptions = rule.puissanceOptions.split(',')
        .map(s => s.trim())
        .filter(s => s !== '')
        .map(s => s.toLowerCase().endsWith('kva') ? s : `${s} KVA`);
      this.puissanceOptions = pOptions;
      this.puissance2Options = ['N/A', ...pOptions];
    }

    this.syncProjectValuesWithUnit();
  }

  private setDefaultOptions(): void {
    this.voltageOptions = ['10 kV', '15 kV', '17.5 kV', '20 kV'];
    const pBase = ['630 KVA', '800 KVA', '1000 KVA', '1250 KVA', '1600 KVA', '2000 KVA', '2500 KVA'];
    this.puissanceOptions = pBase;
    this.puissance2Options = ['N/A', ...pBase];

    this.syncProjectValuesWithUnit();
  }

  /**
   * Fixes the "empty select" issue by ensuring the project's current values
   * exactly match one of the formatted options (including units).
   */
  private syncProjectValuesWithUnit(): void {
    if (this.project.voltage) {
      const normalized = this.voltageOptions.find(opt =>
        opt.replace(/[^\d.]/g, '') === this.project.voltage?.replace(/[^\d.]/g, '')
      );
      if (normalized && normalized !== this.project.voltage) {
        this.project.voltage = normalized;
      }
    }

    if (this.project.puissanceTransfo1) {
      const normalized = this.puissanceOptions.find(opt =>
        opt.replace(/[^\d.]/g, '') === this.project.puissanceTransfo1?.replace(/[^\d.]/g, '')
      );
      if (normalized && normalized !== this.project.puissanceTransfo1) {
        this.project.puissanceTransfo1 = normalized;
      }
    }

    if (this.project.puissanceTransfo2 && this.project.puissanceTransfo2 !== 'N/A') {
      const normalized = this.puissanceOptions.find(opt =>
        opt.replace(/[^\d.]/g, '') === this.project.puissanceTransfo2?.replace(/[^\d.]/g, '')
      );
      if (normalized && normalized !== this.project.puissanceTransfo2) {
        this.project.puissanceTransfo2 = normalized;
      }
    }
  }

  updateVoltage(value: string): void {
    this.updateField({ voltage: value });
  }

  updatePuissance1(value: string): void {
    this.updateField({ puissanceTransfo1: value });
  }

  updatePuissance2(value: string): void {
    const patch: Partial<Project> = { puissanceTransfo2: value };
    if (value === 'N/A') {
      patch.typeTransfo2 = 'N/A';
    } else if (!this.project.typeTransfo2 || this.project.typeTransfo2 === 'N/A') {
      patch.typeTransfo2 = 'Huile'; // Default when activating Transfo 2
    }
    this.updateField(patch);
  }

  updateType1(value: string): void {
    this.updateField({ typeTransfo1: value });
  }

  updateType2(value: string): void {
    this.updateField({ typeTransfo2: value });
  }

  openRuleDialog(): void {
    this.showRuleDialog.set(true);
  }

  closeRuleDialog(): void {
    this.showRuleDialog.set(false);
  }

  private updateField(patch: Partial<Project>): void {
    if (!this.project.id) return;

    if (this.readonlyMode) {
      this.error.emit("Vous n'avez pas les droits pour modifier le comptage.");
      return;
    }

    if (this.isProjectLocked()) {
      const message = "Le projet est verrouille (VALIDE/TERMINE). Le comptage ne peut plus etre modifie.";
      this.localErrorMessage.set(message);
      this.error.emit(message);
      return;
    }

    const updatedProject = { ...this.project, ...patch };

    updatedProject.comptage = ComptageUtils.calculateComptageType(
      updatedProject.voltage,
      updatedProject.puissanceTransfo1,
      updatedProject.puissanceTransfo2,
      updatedProject.comptage,
      this.activeRule()
    );

    this.projectService.updateProject(this.project.id, updatedProject).subscribe({
      next: (res) => {
        this.project = res;
        this.syncProjectValuesWithUnit();
        this.snackBar.open('Comptage mis à jour', 'Fermer', { duration: 3000, horizontalPosition: 'right', verticalPosition: 'bottom' });
        this.localErrorMessage.set('');
        this.saved.emit();
        this.cdr.markForCheck();
      },
      error: (err) => {
        const message = err?.error?.message || err?.error || 'Erreur lors de la mise a jour du comptage';
        this.localErrorMessage.set(message);
        this.error.emit(message);
      }
    });
  }

  isProjectLocked(): boolean {
    return this.project?.metaStatus !== 'DRAFT';
  }
}
